import { Topbar } from '@/components/organisms/Topbar';
import { Skeleton } from '@/components/ui/skeleton';
import { EmailAccountsSkeletonRows, SKELETON_COLS, SKELETON_ROWS } from './EmailAccountsSkeletonRows';

const CRUMBS = [{ label: 'Workspace', to: '/email-accounts' }, { label: 'Email Accounts' }];

/**
 * Skeleton shaped like the accounts page — reused both as `EmailAccountsPage`'s
 * own `isPending` branch (query still resolving after mount) and as the
 * `/email-accounts` route's `pendingComponent` (route-transition gap before
 * the loader settles). Kept as a single named export so both call sites stay
 * in lockstep — see `docs/plans/frontend-loader-state-skeletons.md` §6d.
 *
 * Mirrors the loaded page block for block — the toolbar card (search, buttons,
 * seven status tiles), then the table card (header, {@link SKELETON_ROWS}
 * rows, pager) — so nothing jumps when the data lands.
 */
export function EmailAccountsPendingState() {
  return (
    <>
      <Topbar crumbs={CRUMBS} title="Email Accounts" subtitle="Loading your connected accounts…" />
      <div
        className="flex min-h-0 flex-1 flex-col overflow-auto"
        style={{ background: 'var(--canvas)' }}
        aria-busy="true"
        aria-live="polite"
      >
        <div className="flex w-full flex-col gap-3 px-4 py-6 md:px-8 md:py-6" aria-hidden>
          {/* Toolbar card: search + actions, then the status tiles. */}
          <div
            className="flex flex-col gap-3 rounded-[var(--r-lg)] border px-[18px] py-3"
            style={{ background: 'var(--paper)', borderColor: 'var(--ink-200)' }}
          >
            <div className="flex flex-wrap items-center justify-between gap-2.5">
              <Skeleton className="h-8 w-[280px] max-w-full rounded-[var(--r-md)]" />
              <div className="flex items-center gap-2">
                <Skeleton className="h-8 w-[84px] rounded-[var(--r-md)]" />
                <Skeleton className="h-8 w-[84px] rounded-[var(--r-md)]" />
                <Skeleton className="h-8 w-[92px] rounded-[var(--r-md)]" />
              </div>
            </div>
            <div className="overflow-x-hidden">
              <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(7, minmax(112px, 1fr))' }}>
                {Array.from({ length: 7 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex min-h-[60px] flex-col justify-center gap-2 rounded-[var(--r-lg)] border px-3 py-2.5"
                    style={{ borderColor: 'var(--ink-200)' }}
                  >
                    <Skeleton className="h-3 w-16" />
                    <Skeleton className="h-5 w-9" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Table card: header, rows, pager. */}
          <div
            className="overflow-hidden rounded-[13px] border"
            style={{ background: 'var(--paper)', borderColor: 'var(--ink-200)' }}
          >
            <div className="overflow-x-hidden">
              <div
                className="grid min-h-10 items-center gap-6 border-b px-[18px]"
                style={{ gridTemplateColumns: SKELETON_COLS, borderColor: 'var(--ink-100)' }}
              >
                <Skeleton className="size-4 rounded" />
                <Skeleton className="h-3 w-20" />
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-3 w-16" />
                ))}
                <span />
              </div>
              <EmailAccountsSkeletonRows />
            </div>
            <div className="flex items-center justify-between gap-2.5 px-[18px] py-2.5">
              <Skeleton className="h-3 w-40" />
              <div className="flex items-center gap-2">
                <Skeleton className="h-8 w-[68px] rounded-[9px]" />
                <Skeleton className="h-8 w-[180px] rounded-[9px]" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
